import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, FolderOpen, Trash2, Edit3, X, FileText, ArrowLeft, ArrowRight,
  MoreHorizontal, Presentation, PenTool, BarChart3, Table2, Search,
  Calendar, Palette, ExternalLink, Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { toast } from 'sonner';
import { withBackend, normalizeImageUrl, toThumbnailUrl } from '../utils/api';
import { motion, AnimatePresence } from 'framer-motion';

const TYPE_ICONS = {
  'create-ppt': Presentation,
  'presentation': Presentation,
  'infographic': PenTool,
  'document': FileText,
  'dashboard': BarChart3,
  'sheets': Table2,
  'analysis': Search,
  'redesign': Edit3,
};

const TYPE_COLORS = {
  'create-ppt': 'text-green-500',
  'presentation': 'text-green-500',
  'infographic': 'text-purple-500',
  'document': 'text-blue-500',
  'dashboard': 'text-amber-500',
  'sheets': 'text-teal-500',
  'analysis': 'text-gray-500',
  'redesign': 'text-pink-500',
};

function CreateProjectModal({ open, onClose, onCreate, userId, brandIdentities }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [brief, setBrief] = useState('');
  const [brandId, setBrandId] = useState(null);
  const [creating, setCreating] = useState(false);

  if (!open) return null;

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Project name is required'); return; }
    setCreating(true);
    try {
      const res = await fetch(withBackend('/api/project-folders/create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          name: name.trim(),
          description: description.trim() || null,
          brief: brief.trim() || null,
          brand_identity_id: brandId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success('Project created');
      onCreate(data.project);
      onClose();
      setName(''); setDescription(''); setBrief(''); setBrandId(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground">New Project</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground block mb-1.5">Project Name *</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Q4 Marketing Campaign" className="h-11" autoFocus />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground block mb-1.5">Description</label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="What is this project about?" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground block mb-1.5">Brief / Goal</label>
            <textarea
              value={brief} onChange={e => setBrief(e.target.value)}
              placeholder="Describe the strategic goal or brief for this project..."
              rows={3}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          {/* Brand Identity selector */}
          {brandIdentities.length > 0 && (
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Brand Identity</label>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setBrandId(null)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    !brandId ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/30'
                  }`}
                >
                  None
                </button>
                {brandIdentities.map(bi => (
                  <button
                    key={bi.id}
                    onClick={() => setBrandId(bi.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      brandId === bi.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/30'
                    }`}
                  >
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: bi.primary_color || '#6366F1' }} />
                    {bi.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="gradient" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating...' : 'Create Project'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ProjectFolderView({ folderId, userId, onBack, onOpenDocument, brandIdentities, allDocuments }) {
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [brandIdentity, setBrandIdentity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [addSearch, setAddSearch] = useState('');

  const fetchFolder = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(withBackend(`/api/project-folders/${folderId}`));
      const data = await res.json();
      setProject(data.project);
      setDocuments(data.documents || []);
      setBrandIdentity(data.brand_identity);
      setEditName(data.project?.name || '');
      setEditDesc(data.project?.description || '');
    } catch (err) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [folderId]);

  useEffect(() => { fetchFolder(); }, [fetchFolder]);

  const handleSaveEdit = async () => {
    try {
      await fetch(withBackend(`/api/project-folders/${folderId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName, description: editDesc }),
      });
      setProject(p => ({ ...p, name: editName, description: editDesc }));
      setEditing(false);
      toast.success('Project updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleRemoveDoc = async (docId) => {
    try {
      await fetch(withBackend(`/api/project-folders/${folderId}/documents/${docId}`), { method: 'DELETE' });
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document removed from project');
    } catch { toast.error('Failed to remove'); }
  };

  const handleAddDoc = async (doc) => {
    try {
      const res = await fetch(withBackend(`/api/project-folders/${folderId}/documents`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_id: doc.id, document_type: doc.type }),
      });
      if (!res.ok) throw new Error('Failed');
      fetchFolder();
      setShowAddDoc(false);
      toast.success(`Added "${doc.name}" to project`);
    } catch { toast.error('Failed to add document'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const existingDocIds = new Set(documents.map(d => d.id));
  const availableDocs = allDocuments.filter(d =>
    !existingDocIds.has(d.id) &&
    (!addSearch || d.name?.toLowerCase().includes(addSearch.toLowerCase()))
  );

  return (
    <div className="min-h-screen max-w-5xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={onBack} className="mt-1 flex-shrink-0">
          <ArrowLeft className="w-4 h-4" />
        </Button>

        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-2">
              <Input value={editName} onChange={e => setEditName(e.target.value)} className="text-xl font-bold h-auto py-1" />
              <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description" className="text-sm" />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveEdit}><Check className="w-3.5 h-3.5 mr-1" /> Save</Button>
                <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground">{project?.name}</h1>
                <button onClick={() => setEditing(true)} className="text-muted-foreground hover:text-foreground">
                  <Edit3 className="w-4 h-4" />
                </button>
              </div>
              {project?.description && <p className="text-sm text-muted-foreground mt-1">{project.description}</p>}
              {project?.brief && <p className="text-xs text-muted-foreground mt-1 italic">Brief: {project.brief}</p>}
            </div>
          )}

          {/* Brand identity badge */}
          {brandIdentity && (
            <div className="flex items-center gap-2 mt-3">
              <div className="flex items-center gap-1.5 bg-primary/5 border border-primary/20 rounded-lg px-2.5 py-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: brandIdentity.primary_color || '#6366F1' }} />
                <span className="text-xs font-medium text-primary">{brandIdentity.name}</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => setShowAddDoc(true)}>
            <Plus className="w-4 h-4 mr-1.5" /> Add Document
          </Button>
        </div>
      </div>

      {/* Add Document Modal */}
      {showAddDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowAddDoc(false)}>
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground">Add Existing Document</h3>
              <button onClick={() => setShowAddDoc(false)}><X className="w-5 h-5 text-muted-foreground" /></button>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={addSearch} onChange={e => setAddSearch(e.target.value)} placeholder="Search documents..." className="pl-9" />
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {availableDocs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No documents available to add.</p>
              ) : (
                availableDocs.map(doc => {
                  const Icon = TYPE_ICONS[doc.type] || FileText;
                  const color = TYPE_COLORS[doc.type] || 'text-gray-500';
                  return (
                    <button
                      key={doc.id}
                      onClick={() => handleAddDoc(doc)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
                    >
                      <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{doc.name || 'Untitled'}</p>
                        <p className="text-xs text-muted-foreground">{doc.type}</p>
                      </div>
                      <Plus className="w-4 h-4 text-muted-foreground" />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Documents Grid */}
      {documents.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <FolderOpen className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No documents yet</h3>
          <p className="text-sm text-muted-foreground mb-6">Add existing documents or create new ones in this project.</p>
          <Button variant="outline" onClick={() => setShowAddDoc(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add Document
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {documents.map(doc => {
              const Icon = TYPE_ICONS[doc.document_type || doc.type] || FileText;
              const color = TYPE_COLORS[doc.document_type || doc.type] || 'text-gray-500';

              return (
                <motion.div
                  key={doc.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <Card className="group cursor-pointer hover:shadow-card-hover hover:border-primary/20 overflow-hidden"
                    onClick={() => onOpenDocument?.(doc.id)}
                  >
                    {/* Thumbnail */}
                    <div className="relative h-32 bg-muted overflow-hidden">
                      {doc.thumbnail ? (
                        <img
                          src={
                            doc.thumbnail.startsWith('/')
                              ? withBackend(doc.thumbnail)
                              : (toThumbnailUrl(doc.thumbnail) || '')
                          }
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
                          <Icon className={`w-8 h-8 ${color} opacity-40`} />
                        </div>
                      )}

                      {/* Type badge */}
                      <div className="absolute top-2 left-2">
                        <Badge variant="secondary" className="text-[10px] gap-1 bg-background/80 backdrop-blur-sm">
                          <Icon className={`w-3 h-3 ${color}`} />
                          {doc.document_type || doc.type}
                        </Badge>
                      </div>

                      {/* Actions */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 bg-background/60 backdrop-blur-sm" onClick={e => e.stopPropagation()}>
                              <MoreHorizontal className="w-3.5 h-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={e => { e.stopPropagation(); onOpenDocument?.(doc.id); }}>
                              <ExternalLink className="w-4 h-4 mr-2" /> Open
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={e => { e.stopPropagation(); handleRemoveDoc(doc.id); }}
                              className="text-destructive focus:text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" /> Remove from project
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    <div className="p-4">
                      <h3 className="font-semibold text-sm text-foreground truncate group-hover:text-primary transition-colors">
                        {doc.name || 'Untitled'}
                      </h3>
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : 'Unknown'}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export function ProjectsPage({ userId, onBack, onOpenDocument, brandIdentities = [], allDocuments = [], initialFolderId = null, onInitialFolderConsumed }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  // Seeded when the sidebar deep-opens a specific folder. Consuming NOTIFIES
  // App to clear the request — otherwise a later remount of this page (user
  // went to Brands and came back via "See all") reopens the stale folder.
  const [viewingFolderId, setViewingFolderId] = useState(initialFolderId);
  useEffect(() => {
    if (initialFolderId) {
      setViewingFolderId(initialFolderId);
      onInitialFolderConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFolderId]);
  const [deletingId, setDeletingId] = useState(null);

  const fetchProjects = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetch(withBackend(`/api/project-folders?user_id=${userId}`));
      const data = await res.json();
      setProjects(data.projects || []);
    } catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project? Documents inside will not be deleted.')) return;
    setDeletingId(id);
    try {
      await fetch(withBackend(`/api/project-folders/${id}`), { method: 'DELETE' });
      setProjects(prev => prev.filter(p => p.id !== id));
      toast.success('Project deleted');
    } catch { toast.error('Failed to delete'); }
    finally { setDeletingId(null); }
  };

  // Folder view
  if (viewingFolderId) {
    return (
      <ProjectFolderView
        folderId={viewingFolderId}
        userId={userId}
        onBack={() => { setViewingFolderId(null); fetchProjects(); }}
        onOpenDocument={onOpenDocument}
        brandIdentities={brandIdentities}
        allDocuments={allDocuments}
      />
    );
  }

  // List view
  return (
    <div className="min-h-screen max-w-5xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack} className="mr-1">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-foreground">Projects</h1>
            <p className="text-sm text-muted-foreground mt-1">Organize your documents into projects.</p>
          </div>
        </div>
        <Button variant="gradient" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-2" /> New Project
        </Button>
      </div>

      <CreateProjectModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={(p) => { setProjects(prev => [p, ...prev]); }}
        userId={userId}
        brandIdentities={brandIdentities}
      />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <FolderOpen className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No projects yet</h3>
          <p className="text-sm text-muted-foreground mb-6">Create a project to organize your documents together.</p>
          <Button variant="gradient" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-2" /> Create Project
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map(project => (
            <Card
              key={project.id}
              className="group cursor-pointer hover:shadow-card-hover hover:border-primary/20 overflow-hidden"
              onClick={() => setViewingFolderId(project.id)}
            >
              {/* Color accent from brand identity */}
              <div className="h-1.5" style={{
                background: project.brand_identity_color
                  ? `linear-gradient(135deg, ${project.brand_identity_color}, ${project.brand_identity_color}88)`
                  : 'linear-gradient(135deg, #6366F1, #A855F7)'
              }} />

              {/* Preview strip: up to 3 contained document thumbnails */}
              {Array.isArray(project.thumbnails) && project.thumbnails.length > 0 && (
                <div className="flex gap-1.5 px-5 pt-4">
                  {project.thumbnails.slice(0, 3).map((t, ti) => (
                    <div key={ti} className="flex-1 min-w-0 aspect-video rounded-md overflow-hidden bg-muted border border-border">
                      <img
                        src={t.startsWith('/') ? withBackend(t) : (toThumbnailUrl(t) || t)}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                      {project.name}
                    </h3>
                    {project.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{project.description}</p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        onClick={e => e.stopPropagation()}>
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={e => { e.stopPropagation(); setViewingFolderId(project.id); }}>
                        <FolderOpen className="w-4 h-4 mr-2" /> Open
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={e => { e.stopPropagation(); handleDelete(project.id); }}
                        className="text-destructive focus:text-destructive" disabled={deletingId === project.id}>
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex items-center gap-3 mt-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {project.document_count || 0} docs
                  </span>
                  {project.brand_identity_name && (
                    <span className="flex items-center gap-1">
                      <Palette className="w-3 h-3" />
                      {project.brand_identity_name}
                    </span>
                  )}
                  <span className="ml-auto flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : ''}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
